import type { DashboardFilters } from '@/pages/CustomDashboards/lib/dashboardFilters';
import { applyDashboardFilters, filtersSignature } from '@/pages/CustomDashboards/lib/dashboardFilters';
import { type ReportRow, postReport } from '@/store/slices/reportingV2/reportingV2.api';
import { useEffect, useState } from 'react';

export interface ConversionBreakdownRow {
  eventName: string;
  fires: number;
}

interface UseConversionBreakdownResult {
  rows: ConversionBreakdownRow[];
  loading: boolean;
  error: Error | null;
  /**
   * Filter keys the API dropped for this breakdown (e.g. campaign_ids,
   * line_item_ids — EVENT carries neither). Surfaced so the modal can say so,
   * same as every other widget's ignored_filters handling.
   */
  ignoredFilters: string[];
}

/**
 * Conversion-ACTION breakdown only (FB-008) — which named conversion events
 * fired, and how many times. Not a campaign breakdown: the canonical EVENT
 * fact table (the only source for event_name) carries no campaign/line-item
 * column at all — confirmed against the ingestion pipeline's own schema, not
 * just this API. Getting "which campaigns drove these" needs a real
 * click/view-to-conversion attribution join, tracked separately.
 */
export const useConversionBreakdown = (filters: DashboardFilters | null): UseConversionBreakdownResult => {
  const [rows, setRows] = useState<ConversionBreakdownRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [ignoredFilters, setIgnoredFilters] = useState<string[]>([]);

  useEffect(() => {
    if (filters == null) {
      setRows([]);
      setError(null);
      setIgnoredFilters([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    postReport(
      applyDashboardFilters(
        {
          dimensions: ['event_name'],
          metrics: ['event_fires'],
          sort: [{ field: 'event_fires', dir: 'desc' }],
          // Matches the geo/pivot widgets' own breakdown cap (DashboardBuilder.tsx)
          // — the modal's own "Total" comes from the KPI widget's already-correct
          // aggregate query, not from summing these rows, so a truncated page here
          // only risks hiding a long tail of rare actions, never an incorrect total.
          pagination: { limit: 500, offset: 0 },
        },
        filters,
      ),
      controller.signal,
    )
      .then(response => {
        setRows(
          response.data.rows.map((row: ReportRow) => ({
            eventName: String(row.event_name ?? 'Unlabeled'),
            fires: Number(row.event_fires ?? 0),
          })),
        );
        setIgnoredFilters(response.ignored_filters ?? []);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // Signature, not `filters` itself: DashboardBuilder recomputes
    // effectiveFilters (via resolveWidgetFilters) on every render, so a raw
    // object-identity dependency would abort and re-fire on any unrelated
    // re-render while the modal is open. Same convention as the widget fetch
    // effect's own `filtersSig` in DashboardBuilder.tsx.
  }, [filters == null ? null : filtersSignature(filters)]);

  return { rows, loading, error, ignoredFilters };
};
